/**
 * The create-company wizard routes carry the owning organization in the path
 * (`/custom-companies/organizations/:organizationId/create-company/…`), while
 * the sidebar selector reads the selection from the `?organizationId=` query
 * param. When a URL has the path but no param — a bookmark, a pasted link, or a
 * navigation that dropped the search string — the path is the more reliable
 * signal: it is the organization the page is actually about, and it is the one
 * the company gets created for.
 *
 * This module holds both halves of that fallback — reading the organization out
 * of the path, and deciding whether to write it back into the param. Plain
 * string functions rather than react-router's `matchPath` and hook state, so
 * the selected-organization provider can call them during render (it sits above
 * the routes that declare `:organizationId`, so `useParams` cannot see it) and
 * so both rules are unit-testable on their own.
 */
const WIZARD_ORGANIZATION_PATH = /^\/custom-companies\/organizations\/(\d+)(?:\/|$)/;

/** The organization id in a custom-company wizard pathname, if it has one. */
export function organizationIdFromPath(pathname: string): string | undefined {
  return WIZARD_ORGANIZATION_PATH.exec(pathname)?.[1];
}

/**
 * The path organization to write into the `?organizationId=` param, or
 * `undefined` when the URL should be left as it is.
 *
 * Persisting is what carries the selection back out of the wizard: the
 * completion step navigates to `/custom-companies`, which keeps the search
 * string but not the path, so without the param the selector would snap to the
 * default. It is only worth doing when the user has a selection to lose —
 * the sidebar selector renders nothing below two organizations, and a
 * single-organization user's default already resolves to that organization, so
 * the param would be noise in every URL they share.
 */
export function pathOrganizationToPersist({
  organizationIdParam,
  pathOrganizationId,
  selectableOrganizationIds,
}: {
  /** Current `?organizationId=` value; `null` when the param is absent. */
  organizationIdParam: string | null;
  /** Organization named in the pathname, from {@link organizationIdFromPath}. */
  pathOrganizationId: string | undefined;
  /** Ids of the organizations the sidebar selector offers. */
  selectableOrganizationIds: readonly number[];
}): string | undefined {
  // Any value counts as set, `none` included, so persisting can never override a
  // deliberate "None" selection.
  if (organizationIdParam != null) {
    return undefined;
  }
  if (pathOrganizationId == null || selectableOrganizationIds.length <= 1) {
    return undefined;
  }
  // An id the user has no access to is not a selection worth recording.
  return selectableOrganizationIds.some(id => String(id) === pathOrganizationId) ? pathOrganizationId : undefined;
}
